import {
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTracking } from '../../../../react-components/tracking';

/**
 * An icon button that debounces clicks for a given delay to
 * prevent click spamming.
 */
export interface IconButtonProps extends MuiIconButtonProps {
  /**
   * Delay in milliseconds to debounce button clicks
   * @default 500
   */
  delay?: number;
  /**
   * Whether the button is debounced
   * @default false
   */
  debounced?: boolean;
  /**
   * The function to call when the button is clicked
   *
   * @param event The mouse event
   * @returns void
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Optional tracking name for DataDog tracking. If not provided, defaults to 'icon-button'
   */
  trackingName?: string;
  /**
   * Optional label text to display below the button, centered
   */
  label?: string;
  /**
   * Whether the button is in an active state (applies solid background colors)
   * @default false
   */
  active?: boolean;
}

export const IconButton = ({
  delay = 500,
  disabled = false,
  debounced = false,
  children,
  onClick,
  trackingName,
  label,
  active = false,
  ...props
}: IconButtonProps) => {
  const [isDebounced, setIsDebounced] = useState<boolean>(false);
  const { generateTrackingProps } = useTracking();
  const theme = useTheme();

  // Generate tracking props with fallback to 'icon-button' if no trackingName provided
  const trackingProps = generateTrackingProps(trackingName || 'icon-button');

  useEffect(() => {
    if (!isDebounced) {
      return;
    }

    const rebounce = setTimeout(() => {
      setIsDebounced(false);
    }, delay);
    return () => clearTimeout(rebounce);
  }, [isDebounced, delay, disabled]);

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    if (isDebounced) {
      return;
    }

    if (debounced) {
      setIsDebounced(true);
    }

    if (onClick) {
      return onClick(e);
    }
  }

  // Get icon and background color styles based on color prop
  const getColorStyles = () => {
    const color = props.color || 'default';

    const colorMap: Record<
      string,
      {
        backgroundColor: string;
        color: string;
        iconColor: string;
        iconColorInactive: string;
      }
    > = {
      default: {
        backgroundColor: theme.palette.greyShades?.[300] || '#e0e0e0',
        color: theme.palette.greyShades?.[900] || '#212121',
        iconColor: theme.palette.greyShades?.[900] || '#212121',
        iconColorInactive: theme.palette.greyShades?.[900] || '#212121',
      },
      primary: {
        backgroundColor: theme.palette.primary?.main || '#1976d2',
        color: theme.palette.primary?.contrastText || '#fff',
        iconColor: theme.palette.primary?.contrastText || '#fff',
        iconColorInactive: theme.palette.primary?.main || '#1976d2',
      },
      secondary: {
        backgroundColor: theme.palette.secondary?.main || '#dc004e',
        color: theme.palette.secondary?.contrastText || '#fff',
        iconColor: theme.palette.secondary?.contrastText || '#fff',
        iconColorInactive: theme.palette.secondary?.main || '#dc004e',
      },
      error: {
        backgroundColor: theme.palette.error?.main || '#d32f2f',
        color: theme.palette.primary?.contrastText || '#fff',
        iconColor: theme.palette.primary?.contrastText || '#fff',
        iconColorInactive: theme.palette.error?.main || '#d32f2f',
      },
      info: {
        backgroundColor: theme.palette.info?.main || '#0288d1',
        color: theme.palette.info?.contrastText || '#fff',
        iconColor: theme.palette.info?.contrastText || '#fff',
        iconColorInactive: theme.palette.info?.main || '#0288d1',
      },
      success: {
        backgroundColor: theme.palette.success?.main || '#2e7d32',
        color: theme.palette.success?.contrastText || '#fff',
        iconColor: theme.palette.success?.contrastText || '#fff',
        iconColorInactive: theme.palette.success?.main || '#2e7d32',
      },
      warning: {
        backgroundColor: theme.palette.warning?.main || '#ed6c02',
        color: theme.palette.warning?.contrastText || '#fff',
        iconColor: theme.palette.warning?.contrastText || '#fff',
        iconColorInactive: theme.palette.warning?.main || '#ed6c02',
      },
      inherit: {
        backgroundColor: 'inherit',
        color: 'inherit',
        iconColor: 'inherit',
        iconColorInactive: 'inherit',
      },
    };

    const styles = colorMap[color] || colorMap.default;
    const currentIconColor = active
      ? styles.iconColor
      : styles.iconColorInactive;

    return {
      // Only apply solid background when active
      ...(active && {
        backgroundColor: styles.backgroundColor,
        color: styles.color,
      }),
      // Always apply icon color based on color prop
      '& svg': {
        fill: currentIconColor,
        color: currentIconColor,
      },
      '& path': {
        fill: currentIconColor,
        stroke: currentIconColor,
      },
    };
  };

  const button = (
    <MuiIconButton
      disabled={isDebounced || disabled}
      onClick={onClick ? handleClick : null}
      {...props}
      {...trackingProps}
      sx={{
        backgroundColor: active
          ? undefined
          : theme.palette.greyShades?.[225] || '#f5f5f5',
        '&:hover': {
          backgroundColor: active
            ? undefined
            : theme.palette.greyShades?.[250] || '#eeeeee',
        },
        ...getColorStyles(),
        ...props.sx,
      }}
    >
      {children}
    </MuiIconButton>
  );

  if (label) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {button}
        <Typography
          variant="caption"
          sx={{
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  }

  return button;
};
